// Enganche y pago semanal ligados: uno va fijo y el otro se calcula para el plazo elegido.
// Puro, sin React.
import { quoteWeeklyFromDownPayment } from '@/shared/domain/quote';
import { formatMXN } from '@/shared/lib/format/money';

/** El valor que el vendedor fijó; el otro se calcula. */
export type FixedField = 'down' | 'pay';

/** Al entrar: enganche fijo, se calcula el pago semanal. */
export const INITIAL_FIXED: FixedField = 'down';

export interface LinkedTermsContext {
    minDown: number;
    maxDown: number;
    /** Plazos válidos del plan. */
    terms: readonly number[];
    /** Pago semanal (pesos, con IVA) para un enganche y un plazo; `Infinity` si no se puede cotizar. */
    weeklyPayment: (down: number, weeks: number) => number;
}

export interface LinkedTerms {
    down: number;
    weeks: number;
    /** Aviso breve cuando algo se topó; `null` si no. */
    notice: string | null;
}

const money = (pesos: number): string => formatMXN(pesos);
const toCents = (pesos: number): number => Math.round(pesos * 100);

/** Topa el enganche en su mínimo y máximo, al centavo. */
export function clampDown(
    down: number,
    ctx: LinkedTermsContext,
): { down: number; notice: string | null } {
    const cents = Math.min(Math.max(toCents(down), toCents(ctx.minDown)), toCents(ctx.maxDown));
    const clamped = cents / 100;
    if (cents === toCents(down)) return { down: clamped, notice: null };
    return {
        down: clamped,
        notice: `El enganche va de ${money(ctx.minDown)} a ${money(ctx.maxDown)}; quedó en ${money(clamped)}.`,
    };
}

/** El plazo válido más cercano; en empate, el más corto. */
export function nearestTerm(weeks: number, terms: readonly number[]): number {
    return [...terms]
        .sort((a, b) => a - b)
        .reduce((best, t) => (Math.abs(t - weeks) < Math.abs(best - weeks) ? t : best));
}

/** Pago semanal más bajo (enganche máximo) y más alto (enganche mínimo) a ese plazo. */
export function paymentRange(weeks: number, ctx: LinkedTermsContext): { min: number; max: number } {
    return {
        min: ctx.weeklyPayment(ctx.maxDown, weeks),
        max: ctx.weeklyPayment(ctx.minDown, weeks),
    };
}

/**
 * Enganche mínimo (al centavo) cuyo pago semanal no pasa del fijado. Si el pago no se alcanza a
 * ese plazo, el enganche se topa y el aviso dice el pago más alto o más bajo posible.
 */
export function solveDownForFixedPayment(
    pay: number,
    weeks: number,
    ctx: LinkedTermsContext,
): LinkedTerms {
    const at = (down: number): number => ctx.weeklyPayment(down, weeks);
    const range = paymentRange(weeks, ctx);
    if (toCents(pay) > toCents(range.max)) {
        return {
            down: ctx.minDown,
            weeks,
            notice: `A ${String(weeks)} semanas el pago más alto es ${money(range.max)}.`,
        };
    }
    if (toCents(pay) < toCents(range.min)) {
        return {
            down: ctx.maxDown,
            weeks,
            notice: `A ${String(weeks)} semanas el pago más bajo es ${money(range.min)}.`,
        };
    }
    let lo = toCents(ctx.minDown);
    let hi = toCents(ctx.maxDown);
    if (at(lo / 100) <= pay) return { down: lo / 100, weeks, notice: null };
    while (hi - lo > 1) {
        const mid = Math.floor((lo + hi) / 2);
        if (at(mid / 100) <= pay) hi = mid;
        else lo = mid;
    }
    return { down: hi / 100, weeks, notice: null };
}

/** Contexto con la cuota del motor local: precio, cotas del enganche y tasa de la cotización. */
export function linkedTermsContext(input: {
    price: number;
    minDown: number;
    maxDown: number;
    terms: readonly number[];
    tna: () => number;
    openingFeePct?: number;
    vatRate?: number;
}): LinkedTermsContext {
    const { price, minDown, maxDown, terms, tna, openingFeePct, vatRate } = input;
    return {
        minDown,
        maxDown,
        terms,
        weeklyPayment: (down, weeks) => {
            const q = quoteWeeklyFromDownPayment({
                price,
                downPayment: down,
                weeks,
                tna: tna(),
                openingFeePct,
                vatRate,
            });
            return q.valid ? (q.weeks[0]?.payment ?? Infinity) : Infinity;
        },
    };
}

/** Lo que el vendedor tiene en pantalla: cuál va fijo, el pago fijado, enganche y plazo. */
export interface LinkedQuoteState {
    fixed: FixedField;
    /** Pago semanal fijado; solo cuenta mientras el pago es el fijo. */
    payTarget: number | null;
    down: number;
    weeks: number;
}

export type LinkedQuoteChange =
    | { kind: 'down'; down: number }
    | { kind: 'weeks'; weeks: number }
    | { kind: 'pay'; pay: number };

/** Siguiente estado tras mover un control: fijar enganche o pago recalcula el otro para el plazo. */
export function applyLinkedChange(
    state: LinkedQuoteState,
    change: LinkedQuoteChange,
    ctx: LinkedTermsContext,
): LinkedQuoteState & { notice: string | null } {
    if (change.kind === 'down') {
        const clamped = clampDown(change.down, ctx);
        return {
            fixed: 'down',
            payTarget: null,
            down: clamped.down,
            weeks: state.weeks,
            notice: clamped.notice,
        };
    }
    if (change.kind === 'pay') {
        return {
            fixed: 'pay',
            payTarget: change.pay,
            ...solveDownForFixedPayment(change.pay, state.weeks, ctx),
        };
    }
    // El plazo cambia solo el valor calculado: el fijo se queda.
    const weeks = nearestTerm(change.weeks, ctx.terms);
    if (state.fixed === 'pay' && state.payTarget !== null) {
        return { ...state, ...solveDownForFixedPayment(state.payTarget, weeks, ctx) };
    }
    const clamped = clampDown(state.down, ctx);
    return { ...state, down: clamped.down, weeks, notice: clamped.notice };
}

/** «Fijar este»: el pago fijo arranca en el que se ve; sin pago a la vista no cambia nada. */
export function fixLinkedField(
    field: FixedField,
    shownPay: number | null,
): Pick<LinkedQuoteState, 'fixed' | 'payTarget'> | null {
    if (field === 'down') return { fixed: 'down', payTarget: null };
    if (shownPay === null) return null;
    return { fixed: 'pay', payTarget: Math.round(shownPay * 100) / 100 };
}
